import { computed, effect, inject, Injectable, signal, untracked } from "@angular/core";
import { filter, firstValueFrom, map, Observable, switchMap, takeUntil, timer } from "rxjs";

import { ConsumptionPlan, PaymentStatus } from "@libs/shares/models/subscription.model";
import { EntityVerificationStatus } from "@libs/shares/dto/entityVerificationDto";
import { SubscriptionService } from "../../domain/subscription/subscription.service";
import { CrmService } from "../../domain/crm/crm.service";
import { PlanConfigurationPriceService } from "../../domain/price/plan-configuration-price.service";
import { PaymentService } from "../../domain/payment/payment.service";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ApiProductFormType } from "../../ui/api-product-card/api-product-card.component";
import { mappedDescriptionPublic, mappedNamePublic, Product, topUpStepUnit } from "@libs/shares/models/models";
import { multipleValidator, rpsValidator } from "../../ui/form/validators";

export enum CheckOutSteps {
  addons = 0,
  entityVerification = 1,
  billingInfo = 2,
  payment = 3
}

@Injectable({ providedIn: "root" })
export class PlanConfigurationUiService {
  private readonly subscriptionService = inject(SubscriptionService);
  private paymentService = inject(PaymentService);
  private readonly planConfigurationPriceService = inject(PlanConfigurationPriceService);
  private readonly crmService = inject(CrmService);

  private _checkoutStep = signal(CheckOutSteps.addons);

  get checkOutStep() {
    return this._checkoutStep.asReadonly();
  }

  pendingSubscription = this.subscriptionService.pendingSubscription;

  isVerified = computed(() => {
    const verificationInfo = this.crmService.verifiedInfoForPayment();
    return verificationInfo?.status === EntityVerificationStatus.approved;
  });

  hasPendingCustomSubscription = computed(() => {
    const pendingSubscription = this.pendingSubscription();
    if (pendingSubscription?.plan === ConsumptionPlan.custom) {
      return true;
    }
    return false;
  });

  pendingSubsscriptionPlanName = computed(() => {
    return this.pendingSubscription()?.plan;
  });

  hasPayment = computed(() => {
    const hasPendingSubscription = this.hasPendingCustomSubscription();
    const pendingSubscription = this.pendingSubscription();
    if (hasPendingSubscription) {
      return pendingSubscription?.payments.some((payment) => payment.status === PaymentStatus.sent);
    }
    return false;
  });

  private getForm(product: Product, rps: number, callLimit: number): ApiProductFormType {
    return new FormGroup({
      product: new FormControl<Product>(product, { nonNullable: true }),
      callLimit: new FormControl(callLimit, {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.min(topUpStepUnit),
          Validators.max(topUpStepUnit * 1000),
          multipleValidator(100_000)
        ]
      }),
      rps: new FormControl(rps, {
        nonNullable: true,
        validators: [Validators.required, rpsValidator]
      })
    });
  }

  readonly allForms = new FormArray<ApiProductFormType>([]);

  private readonly allApis = computed(() => {
    const allAPisConfig = this.planConfigurationPriceService.getAllProductConfigPrice.value();
    if (!allAPisConfig) {
      return [];
    }
    return allAPisConfig.map((productConfig) => {
      const form = this.getForm(productConfig.product, productConfig.rps, productConfig.callLimit);
      form.valueChanges.subscribe((value) => {
        if (form.invalid) {
          return;
        }
        this.planConfigurationPriceService.updateSelectedProducts({
          product: value.product!,
          callLimit: value.callLimit!,
          rps: value.rps!
        });
      });
      return {
        title: mappedNamePublic[productConfig.product].endsWith(" API")
          ? mappedNamePublic[productConfig.product].slice(0, -4)
          : mappedNamePublic[productConfig.product],
        descriptionKey: mappedDescriptionPublic[productConfig.product],
        formGroup: form
      };
    });
  });

  selectedInterval = this.planConfigurationPriceService.billingInterval;
  selectedProducts = this.planConfigurationPriceService.selectedProducts;

  selectedApis = computed(() => {
    const selectedProducts = this.selectedProducts();
    return this.allApis().filter((api) =>
      selectedProducts.map((p) => p.product).includes(api.formGroup.value.product!)
    );
  });

  unselectedApis = computed(() => {
    const selectedProducts = this.selectedProducts();
    return this.allApis().filter(
      (api) => !selectedProducts.map((p) => p.product).includes(api.formGroup.value.product!)
    );
  });

  private onAllIpsUpdate = effect(() => {
    const allApis = this.allApis();
    if (allApis) {
      this.allForms.clear();
      allApis.forEach((api) => {
        this.allForms.push(api.formGroup);
      });
    }
  });

  async invalidateSubscriptions() {
    await this.subscriptionService.getSubscriptions();
  }

  gotoStep(checkoutStep: CheckOutSteps) {
    this._checkoutStep.set(checkoutStep);
  }

  checkPaymentPoller(paymentId: string, intervalMs: number, timeoutMs: number) {
    const timeout = timer(timeoutMs);
    return new Observable<boolean>((subscriber) => {
      const timeoutSubscription = timeout.subscribe(() => subscriber.next(false));

      const poller = timer(intervalMs, intervalMs).pipe(
        switchMap(() => this.paymentService.getPaymentById(paymentId)),
        map((payment) => payment.status === PaymentStatus.paid),
        filter((paid) => paid),
        takeUntil(timeout)
      );
      const pollerSubscription = poller.subscribe({
        next: (data) => subscriber.next(data),
        complete: () => {
          subscriber.complete();
        }
      });

      return () => {
        pollerSubscription.unsubscribe();
        timeoutSubscription.unsubscribe();
      };
    });
  }

  async cancelPendingSubscription() {
    const pendingSubscription = this.pendingSubscription();
    if (!this.hasPendingCustomSubscription || !pendingSubscription) {
      return;
    }
    await firstValueFrom(this.subscriptionService.cancelSubscription(pendingSubscription.id));
    await this.invalidateSubscriptions();
  }

  private verificationStateUpdateEffect = effect(() => {
    const currentStep = this.checkOutStep();
    const isVerified = this.isVerified();
    if (isVerified && currentStep === CheckOutSteps.entityVerification) {
      this.gotoStep(CheckOutSteps.billingInfo);
    }
  });

  private addonsStateUpdateEffect = effect(() => {
    const hasPendingSubscription = this.hasPendingCustomSubscription();
    const currentStep = this.checkOutStep();

    if (hasPendingSubscription && currentStep === CheckOutSteps.addons) {
      this.gotoStep(CheckOutSteps.entityVerification);
    }
  });

  private billingInfoStateUpdateEffect = effect(() => {
    const hasPendingSubscription = this.hasPendingCustomSubscription();
    const hasPayment = this.hasPayment();
    const currentStep = this.checkOutStep();
    if (hasPendingSubscription && hasPayment && currentStep === CheckOutSteps.billingInfo) {
      this.gotoStep(CheckOutSteps.payment);
    }
  });

  private initializeFromPendingSubscription = effect(() => {
    const hasPendingSubscription = this.hasPendingCustomSubscription();
    const pendingSubscription = untracked(() => this.pendingSubscription());
    if (hasPendingSubscription && pendingSubscription) {
      this.planConfigurationPriceService.billingInterval.set(pendingSubscription.billingIntervalInMonths);
      if (pendingSubscription.addons) {
        this.planConfigurationPriceService.updateAddOnConfig({
          apiKeyCount: pendingSubscription.addons?.apiKey,
          applicationCount: pendingSubscription.addons?.application,
          userCount: pendingSubscription.addons?.user
        });
      }

      if (pendingSubscription.productConfiguration) {
        pendingSubscription.productConfiguration.forEach((productConfig) => {
          this.planConfigurationPriceService.updateSelectedProducts(productConfig);
        });
      }
    }
  });
}
